/**
 * A soft dice-rattle sound for the Recovery Board, produced entirely in the
 * browser through the ONE shared AudioContext (see `audio-context.ts`) — no
 * media file to download on mobile data.
 *
 * The sound mirrors the board's tumble timing (8 ticks ~70ms apart) so the
 * pips and the taps feel like the same throw, ending with a small settle
 * "thunk" as the dice comes to rest. It is a transient effect, not playback:
 * it never claims the audio bus, so a running meditation is not interrupted.
 */

import { getAudioContext, unlockAudio } from "@/lib/audio-context";

const TICKS = 8;
const TICK_MS = 70;
const SETTLE_AT_MS = TICKS * TICK_MS + 60;

let noiseBuffer: AudioBuffer | null = null;

function getNoiseBuffer(ctx: AudioContext): AudioBuffer {
  if (noiseBuffer) return noiseBuffer;
  const seconds = 0.06;
  const buf = ctx.createBuffer(1, Math.ceil(ctx.sampleRate * seconds), ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i++) {
    data[i] = Math.random() * 2 - 1;
  }
  noiseBuffer = buf;
  return buf;
}

/** One short filtered tap, like a die knocking against the board. */
function tap(ctx: AudioContext, at: number, level: number): void {
  const src = ctx.createBufferSource();
  src.buffer = getNoiseBuffer(ctx);
  const bp = ctx.createBiquadFilter();
  bp.type = "bandpass";
  bp.frequency.setValueAtTime(500 + Math.random() * 1100, at);
  bp.Q.value = 1.4;
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, at);
  g.gain.exponentialRampToValueAtTime(level, at + 0.006);
  g.gain.exponentialRampToValueAtTime(0.0001, at + 0.07);
  src.connect(bp).connect(g).connect(ctx.destination);
  src.start(at);
  src.stop(at + 0.09);
}

/** A lower, softer thump for the settle. */
function settle(ctx: AudioContext, at: number): void {
  const osc = ctx.createOscillator();
  osc.type = "sine";
  osc.frequency.setValueAtTime(160, at);
  osc.frequency.exponentialRampToValueAtTime(80, at + 0.12);
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, at);
  g.gain.exponentialRampToValueAtTime(0.05, at + 0.012);
  g.gain.exponentialRampToValueAtTime(0.0001, at + 0.16);
  osc.connect(g).connect(ctx.destination);
  osc.start(at);
  osc.stop(at + 0.18);
}

/** Play the dice rattle. Call synchronously from the tap that starts the roll. */
export function playDiceRattle(): void {
  // Arm iPhone's playback session during the actual tap, not just the Web
  // Audio clock: resuming alone leaves generated sounds muted in silent mode.
  void unlockAudio();
  const ctx = getAudioContext();
  if (!ctx || ctx.state === "closed") return;
  const t0 = ctx.currentTime + 0.01;
  // Tumble taps: quick at first, slightly slowing as the dice loses energy.
  for (let i = 0; i < TICKS; i++) {
    const spacing = TICK_MS / 1000 + (i / TICKS) * 0.03;
    tap(ctx, t0 + i * spacing, i === 0 ? 0.1 : 0.06 + Math.random() * 0.03);
  }
  settle(ctx, t0 + SETTLE_AT_MS / 1000);
}
